export const riskLearningSources = [
  { label: 'CFA Institute｜兩資產組合的變異數與相關係數', url: 'https://rpc.cfainstitute.org/sites/default/files/-/media/documents/book/rf-publication/2009/rfv2009n3.pdf' },
  { label: 'CFA Institute｜最大回撤的計算定義', url: 'https://rpc.cfainstitute.org/sites/default/files/docs/research-reports/monash-report-1_performance-of-the-6040_online.pdf' },
  { label: 'CFA Institute｜Sharpe 比率的定義與限制', url: 'https://rpc.cfainstitute.org/sites/default/files/-/media/documents/code/gips/sharpe-ratio-and-the-information-ratio.pdf' },
];

const finite = value => typeof value === 'number' && Number.isFinite(value);
const inRange = (value, low, high) => finite(value) && value >= low && value <= high;
const cleanZero = value => value === 0 ? 0 : value;

/**
 * Two long-only assets with weights summing to one. Volatility inputs and outputs
 * are percentages on the SAME measurement horizon. variance uses decimal-return
 * units squared (20% volatility corresponds to 0.04 variance), not percentage points.
 * The fixed covariance assumptions describe dispersion, not loss limits or forecasts.
 * Zero-volatility assets are allowed; their covariance contribution is zero.
 */
export function portfolioRisk(parameters = {}) {
  const { weightA, volatilityA, volatilityB, correlation } = parameters || {};
  if (!inRange(weightA, 0, 1) || !inRange(volatilityA, 0, 100)
    || !inRange(volatilityB, 0, 100) || !inRange(correlation, -1, 1)) return null;
  const weightB = cleanZero(1 - weightA);
  const a = weightA * (volatilityA / 100);
  const b = weightB * (volatilityB / 100);
  // Equivalent to a² + b² + 2abρ; avoids subtracting nearly equal positive
  // values when ρ is close to -1, which could otherwise create negative variance.
  const variance = cleanZero((a - b) ** 2 + 2 * a * b * (1 + correlation));
  const volatility = cleanZero(Math.sqrt(variance) * 100);
  const standaloneAverage = cleanZero(weightA * volatilityA + weightB * volatilityB);
  if (![variance, volatility, standaloneAverage].every(finite)) return null;
  return { volatility, standaloneAverage, variance, weightB };
}

/**
 * Compound 1–120 hypothetical period returns, each between -100% and 1000%.
 * No deposits, withdrawals, fees or taxes; returns must share one period frequency.
 * rows includes period 0 with returnRate 0, the opening principal and zero drawdown.
 * Drawdowns are POSITIVE loss percentages relative to the highest value so far,
 * including the opening principal. maxDrawdownPercent retains the worst such loss.
 * recoveryPercent is the gain needed from the current value to that historical peak,
 * not to the opening principal and not a forecast. Zero stays zero and has no finite
 * recovery percentage. Any nonfinite arithmetic result makes the whole model null.
 */
export function drawdownPath(parameters = {}) {
  const { principal, returns } = parameters || {};
  if (!finite(principal) || principal <= 0 || !Array.isArray(returns)
    || returns.length < 1 || returns.length > 120) return null;
  for (const returnRate of returns) if (!inRange(returnRate, -100, 1000)) return null;
  let value = principal;
  let peak = principal;
  let maxDrawdownPercent = 0;
  const rows = [{ period: 0, returnRate: 0, value, peak, drawdownPercent: 0 }];
  for (let index = 0; index < returns.length; index += 1) {
    const returnRate = returns[index];
    value = cleanZero(value * (1 + returnRate / 100));
    if (!finite(value)) return null;
    peak = Math.max(peak, value);
    const drawdownPercent = cleanZero(((peak - value) / peak) * 100);
    if (!finite(drawdownPercent)) return null;
    maxDrawdownPercent = Math.max(maxDrawdownPercent, drawdownPercent);
    rows.push({ period: index + 1, returnRate, value, peak, drawdownPercent });
  }
  const totalReturnPercent = cleanZero(((value - principal) / principal) * 100);
  const recoveryPercent = value === 0 ? null : cleanZero(((peak - value) / value) * 100);
  if (!finite(totalReturnPercent) || (recoveryPercent !== null && !finite(recoveryPercent))) return null;
  return { rows, ending: value, totalReturnPercent, maxDrawdownPercent, recoveryPercent };
}

/**
 * One hypothetical annual expected (or annual arithmetic mean) portfolio return,
 * a constant risk-free rate and same-horizon annual standard deviation, all in %.
 * This does not annualize inputs or infer a Sharpe ratio from CAGR. excessReturn is
 * percentage points; ratio is unitless. Volatility's teaching domain is 0–100%.
 * A zero denominator returns ratio null, including 0/0; negative ratios are retained.
 */
export function sharpeRatio(parameters = {}) {
  const { portfolioReturn, riskFreeRate, volatility } = parameters || {};
  if (!finite(portfolioReturn) || !finite(riskFreeRate) || !inRange(volatility, 0, 100)) return null;
  const excessReturn = cleanZero(portfolioReturn - riskFreeRate);
  if (!finite(excessReturn)) return null;
  const ratio = volatility === 0 ? null : cleanZero(excessReturn / volatility);
  if (ratio !== null && !finite(ratio)) return null;
  return { excessReturn, ratio };
}
